import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2, ShoppingBag, ArrowRight, Truck, ShieldCheck, Lock,
  Minus, Plus, Trash2, Receipt,
} from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/common/Seo";
import { useCart } from "@/lib/cart";
import api from "@/lib/api";
import { formatINR } from "@/lib/format";
import PaymentBadges from "@/components/common/PaymentBadges";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { OtpAuth } from "@/components/auth/OtpAuth";

const FREE_SHIP = 499;
// GSTIN: 2-digit state + 5 letters + 4 digits + 1 letter + 1 entity digit/letter + 'Z' + 1 checksum
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

// Navigate to the Shopify-hosted checkout. Shopify forbids being embedded in an
// iframe, so when the app is framed (preview/embed) we must escape to the
// top-level browsing context; otherwise the frame navigation is blocked and the
// user appears "stuck". A top-targeted anchor navigates the topmost frame even
// across origins; we fall back to a new tab, then to a same-window redirect.
const redirectToCheckout = (url) => {
  const framed = window.self !== window.top;
  if (!framed) {
    window.location.href = url;
    return;
  }
  try {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_top";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (e) {
    const w = window.open(url, "_blank", "noopener");
    if (!w) window.location.href = url;
  }
};

export default function Checkout() {
  const { items, subtotal, updateQty, removeItem } = useCart();
  const [placing, setPlacing] = useState(false);
  const [gstWanted, setGstWanted] = useState(false);
  const [firmName, setFirmName] = useState("");
  const [gstin, setGstin] = useState("");
  const [errors, setErrors] = useState({});
  const [authOpen, setAuthOpen] = useState(false);

  const validateGst = () => {
    const e = {};
    if (!firmName.trim()) e.firmName = "Firm name is required";
    const g = gstin.trim().toUpperCase();
    if (!g) e.gstin = "GSTIN is required";
    else if (!GSTIN_RE.test(g)) e.gstin = "Enter a valid 15-character GSTIN";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const startCheckout = async () => {
    setPlacing(true);
    try {
      const payload = {
        items: items.map((it) => ({ slug: it.slug, variant: it.variant, qty: it.qty })),
      };
      if (gstWanted) {
        payload.gst = { firmName: firmName.trim(), gstin: gstin.trim().toUpperCase() };
      }
      const cart = await api.createCheckout(payload);
      if (cart?.checkoutUrl) {
        // Hand off to Shopify-hosted checkout (payments, shipping, taxes, order).
        // Shopify's checkout cannot be rendered inside an iframe (X-Frame-Options),
        // so if we're framed (e.g. a preview/embed) we break out to the top-level
        // browsing context. In a normal top window this is just a standard redirect.
        redirectToCheckout(cart.checkoutUrl);
        return;
      }
      toast.error("Checkout is unavailable right now. Please try again.");
      setPlacing(false);
    } catch (err) {
      // 401 -> not authenticated: enforce Mobile OTP login before checkout.
      if (err?.response?.status === 401) {
        setPlacing(false);
        setAuthOpen(true);
        return;
      }
      const msg = err?.response?.data?.detail || "Could not start checkout";
      toast.error(typeof msg === "string" ? msg : "Could not start checkout");
      setPlacing(false);
    }
  };

  const proceed = async () => {
    if (items.length === 0) return;
    if (gstWanted && !validateGst()) {
      toast.error("Please complete the GST details to continue.");
      return;
    }
    // Gate every checkout behind website Mobile OTP authentication.
    try {
      const me = await api.accountMe();
      if (!me?.authenticated) {
        setAuthOpen(true);
        return;
      }
    } catch {
      setAuthOpen(true);
      return;
    }
    await startCheckout();
  };

  // After successful OTP auth, close the dialog and continue to checkout automatically.
  const handleAuthed = async () => {
    setAuthOpen(false);
    await startCheckout();
  };

  if (items.length === 0) {
    return (
      <div className="bg-[var(--acx-bg)]">
        <Seo title="Checkout" description="Complete your AutoCareX order." noindex />
        <div className="acx-container flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">
          <ShoppingBag size={44} className="text-white/20" />
          <h1 className="mt-5 font-display text-2xl font-bold">Your cart is empty</h1>
          <p className="mt-2 text-[14px] text-white/55">Add some products before checking out.</p>
          <Link to="/shop" className="mt-6 inline-flex items-center gap-2 rounded-[12px] bg-white px-6 py-3 text-[14px] font-semibold text-black transition-colors hover:bg-white/90">
            Browse Products <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[var(--acx-bg)]">
      <Seo title="Checkout" description="Review your AutoCareX order and continue to secure checkout." noindex />
      <div className="acx-container px-4 py-10 sm:px-6 lg:px-10 lg:py-14">
        <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="font-display text-3xl font-bold tracking-[-0.02em] sm:text-4xl">
          Checkout
        </motion.h1>
        <p className="mt-2 text-[14px] text-white/55">Review your order, then continue to our secure checkout.</p>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_400px]">
          {/* Left: order items */}
          <div>
            <h2 className="mb-4 flex items-center gap-2 font-display text-lg font-bold">
              <ShoppingBag size={18} className="text-[#E11B22]" /> Your items ({items.length})
            </h2>
            <div className="space-y-3" data-testid="checkout-items">
              {items.map((it) => (
                <div key={it.key} data-testid="checkout-item" className="flex items-center gap-4 rounded-[14px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-4">
                  <img src={it.image} alt={it.name} className="h-16 w-16 shrink-0 rounded-[10px] border border-[color:var(--acx-border)] object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[14px] font-semibold leading-tight text-white">{it.name}</p>
                    {it.variant && <p className="text-[12px] text-white/50">{it.variant}</p>}
                    <p className="mt-0.5 text-[12px] text-white/45">{formatINR(it.price)} each</p>

                    {/* Quantity stepper + remove */}
                    <div className="mt-2.5 flex items-center gap-3">
                      <div className="inline-flex items-center rounded-[10px] border border-[color:var(--acx-border)] bg-[var(--acx-bg)]">
                        <button
                          type="button"
                          onClick={() => updateQty(it.key, it.qty - 1)}
                          disabled={it.qty <= 1}
                          aria-label="Decrease quantity"
                          data-testid="checkout-qty-decrease"
                          className="flex h-8 w-8 items-center justify-center rounded-l-[10px] text-white/70 transition-colors hover:bg-white/5 hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
                        >
                          <Minus size={14} />
                        </button>
                        <span data-testid="checkout-qty-value" className="min-w-[28px] select-none text-center text-[14px] font-semibold text-white">{it.qty}</span>
                        <button
                          type="button"
                          onClick={() => updateQty(it.key, it.qty + 1)}
                          aria-label="Increase quantity"
                          data-testid="checkout-qty-increase"
                          className="flex h-8 w-8 items-center justify-center rounded-r-[10px] text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => { removeItem(it.key); toast.success("Item removed"); }}
                        data-testid="checkout-item-remove"
                        className="inline-flex items-center gap-1.5 rounded-[10px] px-2.5 py-1.5 text-[12px] font-medium text-white/45 transition-colors hover:bg-white/5 hover:text-[#E11B22]"
                      >
                        <Trash2 size={14} /> Remove
                      </button>
                    </div>
                  </div>
                  <span className="shrink-0 self-start text-[14px] font-semibold text-white">{formatINR(it.price * it.qty)}</span>
                </div>
              ))}
            </div>

            {/* B2B GST bill */}
            <div className="mt-6 rounded-[14px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-4">
              <label htmlFor="gst-toggle" className="flex cursor-pointer items-start gap-3">
                <Checkbox
                  id="gst-toggle"
                  checked={gstWanted}
                  onCheckedChange={(v) => { setGstWanted(Boolean(v)); setErrors({}); }}
                  data-testid="gst-checkbox"
                  className="mt-0.5 border-white/30 data-[state=checked]:border-[#E11B22] data-[state=checked]:bg-[#E11B22]"
                />
                <span className="select-none">
                  <span className="flex items-center gap-2 text-[14px] font-semibold text-white">
                    <Receipt size={15} className="text-[#E11B22]" /> Get a B2B GST bill for my order
                  </span>
                  <span className="mt-0.5 block text-[12px] text-white/45">Add your firm’s GST details to receive a GST tax invoice.</span>
                </span>
              </label>

              <AnimatePresence initial={false}>
                {gstWanted && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                      <div>
                        <label htmlFor="firm-name" className="mb-1.5 block text-[12px] font-medium text-white/60">Firm Name <span className="text-[#E11B22]">*</span></label>
                        <input
                          id="firm-name"
                          data-testid="gst-firmname-input"
                          value={firmName}
                          onChange={(e) => { setFirmName(e.target.value); if (errors.firmName) setErrors((p) => ({ ...p, firmName: undefined })); }}
                          placeholder="Registered business name"
                          aria-invalid={Boolean(errors.firmName)}
                          className={`w-full rounded-[12px] border bg-[var(--acx-bg)] px-4 py-3 text-[14px] text-white placeholder:text-white/35 focus:outline-none ${errors.firmName ? "border-[#E11B22]" : "border-[color:var(--acx-border)] focus:border-white/30"}`}
                        />
                        {errors.firmName && <p data-testid="gst-firmname-error" className="mt-1 text-[12px] text-[#E11B22]">{errors.firmName}</p>}
                      </div>
                      <div>
                        <label htmlFor="gstin" className="mb-1.5 block text-[12px] font-medium text-white/60">GSTIN <span className="text-[#E11B22]">*</span></label>
                        <input
                          id="gstin"
                          data-testid="gst-gstin-input"
                          value={gstin}
                          onChange={(e) => { setGstin(e.target.value.toUpperCase().slice(0, 15)); if (errors.gstin) setErrors((p) => ({ ...p, gstin: undefined })); }}
                          placeholder="22AAAAA0000A1Z5"
                          maxLength={15}
                          aria-invalid={Boolean(errors.gstin)}
                          className={`w-full rounded-[12px] border bg-[var(--acx-bg)] px-4 py-3 text-[14px] uppercase tracking-wide text-white placeholder:text-white/35 placeholder:normal-case focus:outline-none ${errors.gstin ? "border-[#E11B22]" : "border-[color:var(--acx-border)] focus:border-white/30"}`}
                        />
                        {errors.gstin && <p data-testid="gst-gstin-error" className="mt-1 text-[12px] text-[#E11B22]">{errors.gstin}</p>}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

          </div>

          {/* Right: summary */}
          <div className="lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-[18px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-6">
              <h2 className="font-display text-lg font-bold">Order summary</h2>
              <div className="mt-5 space-y-2 border-t border-[color:var(--acx-border)] pt-4">
                <div className="flex justify-between text-[14px] text-white/60"><span>Subtotal</span><span>{formatINR(subtotal)}</span></div>
                <div className="flex justify-between text-[14px] text-white/60"><span>Shipping &amp; taxes</span><span>Calculated at checkout</span></div>
                {gstWanted && (
                  <div className="flex items-center justify-between text-[13px] text-emerald-300">
                    <span className="flex items-center gap-1.5"><Receipt size={13} /> GST bill</span>
                    <span>Requested</span>
                  </div>
                )}
              </div>
              <button
                onClick={proceed}
                disabled={placing}
                data-testid="proceed-to-checkout-button"
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-[12px] bg-[#E11B22] px-5 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-[#c9151b] disabled:opacity-60"
              >
                {placing ? <Loader2 size={17} className="animate-spin" /> : <><Lock size={16} /> Proceed to Secure Checkout</>}
              </button>
              <div className="mt-4 flex items-center justify-center gap-4 text-[12px] text-white/45">
                <span className="flex items-center gap-1.5"><Truck size={14} /> {subtotal >= FREE_SHIP ? "Free shipping" : `Free over ${formatINR(FREE_SHIP)}`}</span>
                <span className="flex items-center gap-1.5"><ShieldCheck size={14} /> Secure</span>
              </div>
              <div className="mt-4 border-t border-[color:var(--acx-border)] pt-4">
                <p className="mb-2.5 text-[11px] uppercase tracking-[0.16em] text-white/45">Secure checkout — pay your way</p>
                <PaymentBadges showCod />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile OTP authentication gate — required before Shopify checkout */}
      <Dialog open={authOpen} onOpenChange={setAuthOpen}>
        <DialogContent
          className="max-w-md border-[color:var(--acx-border)] bg-[var(--acx-bg)] text-white"
          data-testid="checkout-auth-dialog"
        >
          <div className="flex flex-col items-center text-center">
            <Lock size={26} className="text-[#E11B22]" />
            <h2 className="mt-3 font-display text-xl font-bold">Sign in to continue</h2>
            <p className="mt-1.5 text-[13px] text-white/55">
              Verify your mobile number to proceed to secure checkout.
            </p>
            <OtpAuth onAuthed={handleAuthed} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
